import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { Vitals } from "@/shared/types/encounter";

// Copied verbatim from the workspace. NOT "improved" to swallow NaN: TD-7b is a move, and a
// vitals field that used to hold NaN for garbage input must keep doing so until someone decides
// otherwise on purpose.
const numOrNull = (s: string): number | null => (s.trim() === "" ? null : Number(s));

/** Out-of-range styling — red border + red text on the offending measurement only. */
const vOut = (bad: boolean) =>
  bad ? " border-destructive text-destructive [&_input]:text-destructive" : "";

/**
 * Sinh hiệu — the six measurements taken before the doctor examines (TD-7b, #18800).
 *
 * <p><b>Three props, no state.</b> The values belong to the encounter draft, which the workspace
 * owns and debounce-saves, so lifting them down here would have split one save across two owners.
 * What moved is everything that was only ever about vitals: the out-of-range thresholds, the
 * numeric parsing and the layout.
 *
 * <p>It does NOT render the "Khám khác" free-text box that sits under it on screen. That field is
 * part of the SOAP note, and a vitals panel that knew about the clinical note would be the domain
 * leak §9 warns about — the visual grouping is not the ownership boundary.
 */
export function VitalsPanel({
  vitals,
  onChange,
  disabled,
}: {
  vitals: Vitals;
  onChange: (field: keyof Vitals, value: number | null) => void;
  disabled: boolean;
}) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      <div className="space-y-1.5">
        <Label>Cân nặng (kg)</Label>
        <Input
          disabled={disabled}
          type="number"
          step={0.1}
          min={0}
          value={vitals.weightKg ?? ""}
          inputMode="decimal"
          onChange={(ev) => onChange("weightKg", numOrNull(ev.target.value))}
        />
      </div>
      <div
        className={`space-y-1.5 rounded-md${vOut(
          (vitals.systolicBp != null &&
            (vitals.systolicBp >= 140 || vitals.systolicBp < 90)) ||
            (vitals.diastolicBp != null &&
              (vitals.diastolicBp >= 90 || vitals.diastolicBp < 60))
        )}`}
      >
        <Label>Huyết áp (mmHg)</Label>
        <div className="flex items-center gap-1">
          <Input
            disabled={disabled}
            value={vitals.systolicBp ?? ""}
            onChange={(ev) =>
              onChange("systolicBp", numOrNull(ev.target.value))
            }
            inputMode="numeric"
            maxLength={3}
            className="w-14 px-2"
          />
          <span>/</span>
          <Input
            disabled={disabled}
            value={vitals.diastolicBp ?? ""}
            onChange={(ev) =>
              onChange("diastolicBp", numOrNull(ev.target.value))
            }
            inputMode="numeric"
            maxLength={3}
            className="w-14 px-2"
          />
        </div>
      </div>
      <div
        className={`space-y-1.5 rounded-md${vOut(
          vitals.heartRate != null &&
            (vitals.heartRate < 50 || vitals.heartRate > 110)
        )}`}
      >
        <Label>Mạch (lần/phút)</Label>
        <Input
          disabled={disabled}
          value={vitals.heartRate ?? ""}
          inputMode="numeric"
          onChange={(ev) => onChange("heartRate", numOrNull(ev.target.value))}
        />
      </div>
      <div
        className={`space-y-1.5 rounded-md${vOut(
          vitals.temperatureC != null &&
            (vitals.temperatureC < 35 || vitals.temperatureC >= 38)
        )}`}
      >
        <Label>Nhiệt độ (°C)</Label>
        <Input
          disabled={disabled}
          type="number"
          step={0.1}
          value={vitals.temperatureC ?? ""}
          inputMode="decimal"
          onChange={(ev) => onChange("temperatureC", numOrNull(ev.target.value))}
        />
      </div>
      <div
        className={`space-y-1.5 rounded-md${vOut(
          vitals.respiratoryRate != null &&
            (vitals.respiratoryRate < 12 || vitals.respiratoryRate > 24)
        )}`}
      >
        <Label>Nhịp thở (lần/phút)</Label>
        <Input
          disabled={disabled}
          value={vitals.respiratoryRate ?? ""}
          inputMode="numeric"
          onChange={(ev) =>
            onChange("respiratoryRate", numOrNull(ev.target.value))
          }
        />
      </div>
      <div
        className={`space-y-1.5 rounded-md${vOut(
          vitals.spO2 != null && vitals.spO2 < 95
        )}`}
      >
        <Label>SpO₂ (%)</Label>
        <Input
          disabled={disabled}
          value={vitals.spO2 ?? ""}
          inputMode="numeric"
          onChange={(ev) => onChange("spO2", numOrNull(ev.target.value))}
        />
      </div>
    </div>
  );
}
